import { List, Record, Set } from 'immutable';

import { type OverlayConfig } from 'src/actions/Overlay';
import {
  type CloseOverlayAction,
  type IListOf,
  type OpenOverlayAction,
  type ValueOf,
} from 'src/types';

import actions, { OVERLAY_CONFIG, OVERLAY_TYPES } from '../constants/Overlay';
import { Map } from '../lib/ImmutableUtils';
import { getOverlayId, getOverlayPriority, getOverlaySubtype, isOverlayType } from '../lib/Overlay';

// @ts-expect-error
export const PriorityQueueItem = new Record({
  id: undefined,
  subtype: undefined,
});

const INITIAL_STATE = Map({
  overlays: Map<$TSFixMe>(), // map of id -> overlay object
  types: Map({
    // sets of ids
    [OVERLAY_TYPES.anchored]: Set(),
    [OVERLAY_TYPES.blueBar]: Set(),
    [OVERLAY_TYPES.coachmark]: Set(),
    [OVERLAY_TYPES.modal]: Set(),
    [OVERLAY_TYPES.hamburgerMenu]: Set(),
  }),
  priorities: List() as IListOf<$TSFixMe>, // priority queue of PriorityQueueItems
});
export type OverlayState = typeof INITIAL_STATE;

const findExistingPriority = (id: $TSFixMe, subtype: $TSFixMe) => (item: $TSFixMe) => {
  return item.get('id') === id || (subtype && item.get('subtype') === subtype);
};

function addOverlayToPriorityQueue(state: OverlayState, config: OverlayConfig): OverlayState {
  let update = state;
  const id = getOverlayId(config);
  const priority = getOverlayPriority(config);
  const subtype = getOverlaySubtype(config);

  const existingPriorityIndex = state
    .get('priorities')
    .findIndex(findExistingPriority(id, subtype));

  if (existingPriorityIndex >= 0) {
    // already on list, remove and replace
    update = state.set('priorities', state.get('priorities').delete(existingPriorityIndex));
  }

  let newPriorityIndex = update.get('priorities').findIndex(item => {
    return getOverlayPriority(update.get('overlays').get(item.get('id'))) >= priority;
  });
  newPriorityIndex = newPriorityIndex === -1 ? update.get('priorities').size : newPriorityIndex;
  return update.set(
    'priorities',
    update.get('priorities').insert(newPriorityIndex, new PriorityQueueItem({ id, subtype })),
  );
}

function addOverlayToType(state: OverlayState, config: OverlayConfig): OverlayState {
  return state.setIn(
    ['types', config[OVERLAY_CONFIG.TYPE]],
    state.get('types').get(config[OVERLAY_CONFIG.TYPE]).add(config[OVERLAY_CONFIG.ID]),
  );
}

function openOverlay(state: OverlayState, config: OverlayConfig): OverlayState {
  const id = getOverlayId(config);
  config[OVERLAY_CONFIG.ID] = id;

  let update = addOverlayToPriorityQueue(state, config);
  update = addOverlayToType(update, config);

  return update.setIn(['overlays', id], config);
}

function deleteOverlayById(state: OverlayState, id: $TSFixMe): OverlayState {
  let priorities = state.get('priorities');
  const priorityIndex = state.get('priorities').findIndex(item => item.get('id') === id);
  if (priorityIndex > -1) {
    priorities = priorities.delete(priorityIndex);
  }
  return Map({
    overlays: state.get('overlays').delete(id),
    types: Map({
      [OVERLAY_TYPES.anchored]: state.get('types').get(OVERLAY_TYPES.anchored).delete(id),
      [OVERLAY_TYPES.blueBar]: state.get('types').get(OVERLAY_TYPES.blueBar).delete(id),
      [OVERLAY_TYPES.coachmark]: state.get('types').get(OVERLAY_TYPES.coachmark).delete(id),
      [OVERLAY_TYPES.modal]: state.get('types').get(OVERLAY_TYPES.modal).delete(id),
      [OVERLAY_TYPES.hamburgerMenu]: state.get('types').get(OVERLAY_TYPES.hamburgerMenu).delete(id),
    }),
    priorities,
  });
}

function deleteAllOverlaysOfType(
  state: OverlayState,
  overlayType: ValueOf<typeof OVERLAY_TYPES>,
): OverlayState {
  let update = state;
  state
    .get('types')
    .get(overlayType)
    .forEach(id => {
      update = deleteOverlayById(update, id);
    });
  return update;
}

export default function (
  state: OverlayState = INITIAL_STATE,
  action: OpenOverlayAction | CloseOverlayAction,
): OverlayState {
  switch (action.type) {
    case actions.OPEN_OVERLAY:
      return openOverlay(state, action.payload.overlayConfig);
    case actions.CLOSE_OVERLAY: {
      const id = getOverlayId(action.payload.overlayConfig);
      if (isOverlayType(id)) {
        // @ts-expect-error
        return deleteAllOverlaysOfType(state, id);
      }
      return deleteOverlayById(state, id);
    }
    default:
      return state;
  }
}
